/**
 * Microsoft Graph API Permission Definitions
 * 
 * This module defines all the Graph API permissions used by Impact Agent,
 * including detailed descriptions of what each permission enables and why it's needed.
 * 
 * PHASED PERMISSION STRATEGY:
 * - BASE_SCOPES: Requested on first sign-in (user-consentable)
 * - EXTENDED_SCOPES: Requested via incremental consent when user enables Email/Calendar
 */

export type PermissionCategory = 
  | 'identity' 
  | 'files' 
  | 'mail' 
  | 'calendar' 
  | 'teams' 
  | 'people';

export type ConsentStatus = 'granted' | 'not_granted' | 'requires_admin' | 'unknown';

/**
 * Permission tier for UI grouping
 * - recommended: Core permissions for best experience (Files, Chat, Email)
 * - enhanced: Additional capabilities (Calendar, People)
 * - admin_required: Permissions that need IT admin approval
 */
export type PermissionTier = 'recommended' | 'enhanced' | 'admin_required';

export interface GraphPermission {
  /** The scope string (e.g., "User.Read") */
  scope: string;
  /** Human-readable display name */
  displayName: string;
  /** What category this permission belongs to */
  category: PermissionCategory;
  /** Brief description of what this permission allows */
  description: string;
  /** Detailed explanation of why the agent needs this permission */
  whyNeeded: string;
  /** What features/capabilities this enables in the agent */
  enables: string[];
  /** Potential concerns or what this permission could access */
  considerations: string;
  /** What happens without this permission - fallback behavior */
  withoutFallback: string;
  /** Permission tier for UI grouping */
  tier: PermissionTier;
  /** Whether this permission requires admin consent */
  requiresAdminConsent: boolean;
  /** Whether users can consent to this permission themselves */
  userConsentable: boolean;
  /** Whether this is required for basic app functionality */
  isRequired: boolean;
  /** Whether this is part of base scopes (requested on first sign-in) */
  isBaseScope: boolean;
  /** Whether this is an extended scope (requested via incremental consent) */
  isExtendedScope: boolean;
  /** Link to Microsoft's documentation for this permission */
  docsUrl: string;
}

/**
 * Data source feature toggles
 */
export interface DataSourceFeatures {
  /** Teams chats - enabled by default */
  chats: boolean;
  /** OneDrive/SharePoint files - enabled by default */
  files: boolean;
  /** Email (requires extended consent) - disabled by default */
  email: boolean;
  /** Calendar (requires extended consent) - disabled by default */
  calendar: boolean;
}

/**
 * Default data source features (email/calendar OFF by default)
 */
export const DEFAULT_DATA_SOURCE_FEATURES: DataSourceFeatures = {
  chats: true,
  files: true,
  email: false,
  calendar: false,
};

/**
 * Consent state for tracking user's consent status
 */
export interface ConsentState {
  /** Whether base scopes have been granted */
  baseConsented: boolean;
  /** Timestamp of base consent */
  baseConsentedAt?: string;
  /** Whether extended email scopes have been granted */
  emailConsented: boolean;
  /** Timestamp of email consent */
  emailConsentedAt?: string;
  /** Whether extended calendar scopes have been granted */
  calendarConsented: boolean;
  /** Timestamp of calendar consent */
  calendarConsentedAt?: string;
  /** Whether we're using fallback Chat.Read scope */
  chatReadFallback: boolean;
  /** Whether admin consent is required for email */
  emailRequiresAdmin: boolean;
  /** Whether admin consent is required for calendar */
  calendarRequiresAdmin: boolean;
  /** Last check timestamp */
  lastCheckedAt?: string;
}

/**
 * Default consent state
 */
export const DEFAULT_CONSENT_STATE: ConsentState = {
  baseConsented: false,
  emailConsented: false,
  calendarConsented: false,
  chatReadFallback: false,
  emailRequiresAdmin: false,
  calendarRequiresAdmin: false,
};

/**
 * Incremental consent request result
 */
export interface IncrementalConsentResult {
  /** Whether consent was granted */
  success: boolean;
  /** Error information if failed */
  error?: {
    code: string;
    message: string;
    requiresAdmin: boolean;
  };
  /** Scopes that were granted */
  grantedScopes?: string[];
}

/**
 * Feature availability based on consent
 */
export interface FeatureAvailability {
  /** Whether the feature can be used */
  available: boolean;
  /** Reason why feature is not available */
  reason?: string;
  /** Whether admin consent is required */
  requiresAdmin?: boolean;
  /** Whether the feature is enabled by user */
  enabled?: boolean;
}

/**
 * All Graph API permissions used by Impact Agent
 * 
 * Organized by consent phase:
 * - isBaseScope: true = Requested on first sign-in (user-consentable)
 * - isExtendedScope: true = Requested via incremental consent (may need admin)
 */
export const GRAPH_PERMISSIONS: GraphPermission[] = [
  // ========================================
  // IDENTITY PERMISSIONS
  // ========================================
  {
    scope: 'User.Read',
    displayName: 'Sign in and read user profile',
    category: 'identity',
    description: 'Allows the app to sign you in and read your basic profile information.',
    whyNeeded: 'Required for authentication and to personalize your experience with your name and email.',
    enables: [
      'Sign in to the app',
      'Display your name and profile picture',
      'Identify your account across sessions',
    ],
    considerations: 'Only accesses your basic profile (name, email, photo). Does not access any work content.',
    withoutFallback: 'Cannot use the app - required for sign-in',
    tier: 'recommended',
    requiresAdminConsent: false,
    userConsentable: true,
    isRequired: true,
    isBaseScope: true,
    isExtendedScope: false,
    docsUrl: 'https://learn.microsoft.com/en-us/graph/permissions-reference#userread',
  },
  {
    scope: 'User.ReadBasic.All',
    displayName: 'Read all users\' basic profiles',
    category: 'identity',
    description: 'Allows the app to read basic profile information of all users in your organization.',
    whyNeeded: 'Enables the people picker to find colleagues when configuring tracked people.',
    enables: [
      'Search for colleagues by name',
      'Display colleague names and photos',
      'Build your network of tracked people',
    ],
    considerations: 'Can see basic info (name, email, department) of all users in your organization, but not their private data or files.',
    withoutFallback: 'Manually type colleague names instead of searching',
    tier: 'enhanced',
    requiresAdminConsent: false,
    userConsentable: true,
    isRequired: false,
    isBaseScope: false,
    isExtendedScope: false,
    docsUrl: 'https://learn.microsoft.com/en-us/graph/permissions-reference#userreadbasicall',
  },
  {
    scope: 'People.Read',
    displayName: 'Read your relevant people list',
    category: 'people',
    description: 'Allows the app to read a list of people most relevant to you.',
    whyNeeded: 'Helps suggest colleagues based on who you work with most frequently.',
    enables: [
      'Suggest relevant colleagues',
      'Identify your closest collaborators',
      'Prioritize communications from key people',
    ],
    considerations: 'Microsoft determines relevance based on your communication patterns, meetings, and organizational proximity.',
    withoutFallback: 'No automatic colleague suggestions - manually add people',
    tier: 'enhanced',
    requiresAdminConsent: false,
    userConsentable: true,
    isRequired: false,
    isBaseScope: true,
    isExtendedScope: false,
    docsUrl: 'https://learn.microsoft.com/en-us/graph/permissions-reference#peopleread',
  },
  {
    scope: 'offline_access',
    displayName: 'Maintain access to data',
    category: 'identity',
    description: 'Allows the app to maintain access when you\'re not actively using it.',
    whyNeeded: 'Required for the proactive scheduler to generate summaries on your configured schedule, even when you\'re not online.',
    enables: [
      'Scheduled proactive summaries',
      'Background data refresh',
      'Persistent sign-in state',
    ],
    considerations: 'The app can refresh its access without you signing in again. This is required for scheduled/proactive features.',
    withoutFallback: 'No scheduled summaries - must manually trigger each time',
    tier: 'recommended',
    requiresAdminConsent: false,
    userConsentable: true,
    isRequired: true,
    isBaseScope: true,
    isExtendedScope: false,
    docsUrl: 'https://learn.microsoft.com/en-us/graph/permissions-reference#offline_access',
  },

  // ========================================
  // FILES PERMISSIONS
  // ========================================
  {
    scope: 'Files.Read',
    displayName: 'Read your files',
    category: 'files',
    description: 'Allows the app to read files in your OneDrive.',
    whyNeeded: 'Required to search your files for evidence of work and achievements.',
    enables: [
      'Search files for project updates',
      'Find documents related to achievements',
      'Access shared documents for context',
    ],
    considerations: 'Can read files in your OneDrive. The agent searches based on your configured keywords.',
    withoutFallback: 'Upload documents manually or describe your work in conversation',
    tier: 'recommended',
    requiresAdminConsent: false,
    userConsentable: true,
    isRequired: false,
    isBaseScope: true,
    isExtendedScope: false,
    docsUrl: 'https://learn.microsoft.com/en-us/graph/permissions-reference#filesread',
  },
  {
    scope: 'Files.ReadWrite',
    displayName: 'Read and write your files',
    category: 'files',
    description: 'Allows the app to read and write files in your OneDrive.',
    whyNeeded: 'Required to save your configuration, preferences, and generated summaries to your personal OneDrive.',
    enables: [
      'Store your preferences and settings',
      'Save generated progress summaries',
      'Keep conversation history',
      'Maintain your personal data folder',
    ],
    considerations: 'Can access all files in your OneDrive. The agent only uses a dedicated "progress-agent" folder for app data.',
    withoutFallback: 'Summaries saved to cloud storage - download as files when needed',
    tier: 'recommended',
    requiresAdminConsent: false,
    userConsentable: true,
    isRequired: false,
    isBaseScope: false,
    isExtendedScope: false,
    docsUrl: 'https://learn.microsoft.com/en-us/graph/permissions-reference#filesreadwrite',
  },

  // ========================================
  // MAIL PERMISSIONS (EXTENDED - requires incremental consent)
  // ========================================
  {
    scope: 'Mail.Read',
    displayName: 'Read your mail',
    category: 'mail',
    description: 'Allows the app to read email in your mailbox.',
    whyNeeded: 'Enables the agent to search your emails for evidence of accomplishments, praise, and project updates.',
    enables: [
      'Search emails for achievements',
      'Find positive feedback and praise',
      'Track project-related communications',
      'Identify completed tasks and milestones',
    ],
    considerations: 'Can read all your emails. The agent only searches based on your configured keywords and tracked people - it does not store email content.',
    withoutFallback: 'Forward important emails to the agent or summarize them in conversation',
    tier: 'recommended',
    requiresAdminConsent: false,
    userConsentable: false, // Often requires admin in enterprise tenants
    isRequired: false,
    isBaseScope: false,
    isExtendedScope: true,
    docsUrl: 'https://learn.microsoft.com/en-us/graph/permissions-reference#mailread',
  },
  {
    scope: 'Mail.ReadBasic',
    displayName: 'Read your mail metadata',
    category: 'mail',
    description: 'Allows the app to read email metadata (subject, sender, date) but not content.',
    whyNeeded: 'A lighter alternative to Mail.Read when full access requires admin approval.',
    enables: [
      'See email subjects for context',
      'Identify communications with key people',
      'Track email volume patterns',
    ],
    considerations: 'Can see email metadata but not the actual content of messages. Less powerful but may be allowed when Mail.Read is blocked.',
    withoutFallback: 'No email search - share relevant emails manually',
    tier: 'enhanced',
    requiresAdminConsent: false,
    userConsentable: true,
    isRequired: false,
    isBaseScope: false,
    isExtendedScope: true,
    docsUrl: 'https://learn.microsoft.com/en-us/graph/permissions-reference#mailreadbasic',
  },

  // ========================================
  // CALENDAR PERMISSIONS (EXTENDED - requires incremental consent)
  // ========================================
  {
    scope: 'Calendars.Read',
    displayName: 'Read your calendars',
    category: 'calendar',
    description: 'Allows the app to read events in your calendars.',
    whyNeeded: 'Helps understand meeting context and identify work patterns for better summaries.',
    enables: [
      'See meeting titles for context',
      'Understand your schedule patterns',
      'Correlate achievements with meetings',
      'Identify key presentations or reviews',
    ],
    considerations: 'Can see all calendar events including attendees and locations. The agent uses this to provide context, not to track your activities.',
    withoutFallback: 'Manually mention important meetings in your summaries',
    tier: 'enhanced',
    requiresAdminConsent: false,
    userConsentable: true,
    isRequired: false,
    isBaseScope: false,
    isExtendedScope: true,
    docsUrl: 'https://learn.microsoft.com/en-us/graph/permissions-reference#calendarsread',
  },

  // ========================================
  // TEAMS PERMISSIONS
  // ========================================
  {
    scope: 'Chat.ReadBasic',
    displayName: 'Read basic chat info',
    category: 'teams',
    description: 'Allows the app to read your chat threads and participants.',
    whyNeeded: 'Required to list your chat conversations for selection and tracking.',
    enables: [
      'List your chat conversations',
      'See chat participants',
      'Select chats to monitor',
    ],
    considerations: 'Can see chat thread info but not message content. Used together with ChatMessage.Read for full chat access.',
    withoutFallback: 'Share chat highlights by copying messages to the agent',
    tier: 'recommended',
    requiresAdminConsent: false,
    userConsentable: true,
    isRequired: false,
    isBaseScope: true,
    isExtendedScope: false,
    docsUrl: 'https://learn.microsoft.com/en-us/graph/permissions-reference#chatreadbasic',
  },
  {
    scope: 'ChatMessage.Read',
    displayName: 'Read chat messages',
    category: 'teams',
    description: 'Allows the app to read messages in chats you are part of.',
    whyNeeded: 'Enables searching your Teams chats for work updates, achievements, and praise.',
    enables: [
      'Search chats for accomplishments',
      'Find recognition from colleagues',
      'Track project discussions',
      'Identify completed work items',
    ],
    considerations: 'Can read messages in chats you participate in. The agent only searches based on your configured criteria and does not store message content.',
    withoutFallback: 'Describe your chat conversations in the agent conversation',
    tier: 'recommended',
    requiresAdminConsent: false,
    userConsentable: true,
    isRequired: false,
    isBaseScope: true,
    isExtendedScope: false,
    docsUrl: 'https://learn.microsoft.com/en-us/graph/permissions-reference#chatmessageread',
  },
  {
    scope: 'Chat.Read',
    displayName: 'Read your chat messages (full)',
    category: 'teams',
    description: 'Allows the app to read your 1:1 and group chat messages in Teams.',
    whyNeeded: 'Fallback permission if ChatMessage.Read is insufficient in your tenant.',
    enables: [
      'Full chat message access',
      'Search all chat content',
      'Broader chat access in restrictive tenants',
    ],
    considerations: 'More powerful than ChatMessage.Read. Used as fallback when the primary permission fails.',
    withoutFallback: 'Use ChatMessage.Read if available, otherwise share manually',
    tier: 'enhanced',
    requiresAdminConsent: false,
    userConsentable: true,
    isRequired: false,
    isBaseScope: false,
    isExtendedScope: false,
    docsUrl: 'https://learn.microsoft.com/en-us/graph/permissions-reference#chatread',
  },
  {
    scope: 'Team.ReadBasic.All',
    displayName: 'Read the names and descriptions of teams',
    category: 'teams',
    description: 'Allows the app to read the names of teams you are a member of.',
    whyNeeded: 'Required to list the Teams you belong to for selecting channels to monitor.',
    enables: [
      'List your Teams memberships',
      'Show team names in configuration',
      'Enable team-based content selection',
    ],
    considerations: 'Can see basic info about all teams you belong to, but not the content within them.',
    withoutFallback: 'Manually enter team names for configuration',
    tier: 'recommended',
    requiresAdminConsent: false,
    userConsentable: true,
    isRequired: false,
    isBaseScope: true,
    isExtendedScope: false,
    docsUrl: 'https://learn.microsoft.com/en-us/graph/permissions-reference#teamreadbasicall',
  },
  {
    scope: 'Channel.ReadBasic.All',
    displayName: 'Read channel names and descriptions',
    category: 'teams',
    description: 'Allows the app to read the names of channels in teams you are a member of.',
    whyNeeded: 'Required to list channels within your Teams for selecting which to monitor.',
    enables: [
      'List channels in your teams',
      'Select specific channels to track',
      'Configure channel-based filters',
    ],
    considerations: 'Can see channel names and descriptions, but not the messages within them without additional permissions.',
    withoutFallback: 'Manually enter channel names for configuration',
    tier: 'recommended',
    requiresAdminConsent: false,
    userConsentable: true,
    isRequired: false,
    isBaseScope: true,
    isExtendedScope: false,
    docsUrl: 'https://learn.microsoft.com/en-us/graph/permissions-reference#channelreadbasicall',
  },
  {
    scope: 'ChannelMessage.Read.All',
    displayName: 'Read channel messages',
    category: 'teams',
    description: 'Allows the app to read messages in Teams channels you have access to.',
    whyNeeded: 'Enables searching channel messages for work achievements and project updates.',
    enables: [
      'Search channel messages for achievements',
      'Find recognition in team channels',
      'Track project discussions in channels',
      'Identify team-wide accomplishments',
    ],
    considerations: 'Can read all messages in channels you have access to. This is the most powerful Teams permission. The agent only searches based on your configured criteria.',
    withoutFallback: 'Share channel highlights manually in conversation',
    tier: 'admin_required',
    requiresAdminConsent: true,
    userConsentable: false,
    isRequired: false,
    isBaseScope: false,
    isExtendedScope: false,
    docsUrl: 'https://learn.microsoft.com/en-us/graph/permissions-reference#channelmessagereadall',
  },
];

/**
 * Permission category display information
 */
export const PERMISSION_CATEGORIES: Record<PermissionCategory, { label: string; icon: string; description: string }> = {
  identity: {
    label: 'Identity & Profile',
    icon: 'user',
    description: 'Access to your profile and ability to sign in',
  },
  files: {
    label: 'Files & Storage',
    icon: 'folder',
    description: 'Access to your OneDrive files for storing settings and outputs',
  },
  mail: {
    label: 'Email',
    icon: 'mail',
    description: 'Access to search your emails for achievements',
  },
  calendar: {
    label: 'Calendar',
    icon: 'calendar',
    description: 'Access to view your calendar for context',
  },
  teams: {
    label: 'Microsoft Teams',
    icon: 'message-square',
    description: 'Access to your Teams chats and channels',
  },
  people: {
    label: 'People',
    icon: 'users',
    description: 'Access to your colleagues and organizational relationships',
  },
};

/**
 * Group permissions by category
 */
export function groupPermissionsByCategory(permissions: GraphPermission[]): Map<PermissionCategory, GraphPermission[]> {
  const grouped = new Map<PermissionCategory, GraphPermission[]>();
  
  for (const permission of permissions) {
    const existing = grouped.get(permission.category);
    if (existing) {
      existing.push(permission);
    } else {
      grouped.set(permission.category, [permission]);
    }
  }
  
  return grouped;
}

/**
 * Group permissions by tier
 */
export function groupPermissionsByTier(permissions: GraphPermission[]): Map<PermissionTier, GraphPermission[]> {
  const grouped = new Map<PermissionTier, GraphPermission[]>();
  
  // Initialize in display order
  grouped.set('recommended', []);
  grouped.set('enhanced', []);
  grouped.set('admin_required', []);
  
  for (const permission of permissions) {
    const existing = grouped.get(permission.tier);
    if (existing) {
      existing.push(permission);
    }
  }
  
  return grouped;
}

/**
 * Tier display configuration
 */
export const PERMISSION_TIERS: Record<PermissionTier, { 
  label: string; 
  description: string; 
  icon: string;
  color: string;
}> = {
  recommended: {
    label: 'Recommended Permissions',
    description: 'These permissions enable automated evidence gathering and are strongly recommended for the best experience.',
    icon: 'sparkles',
    color: 'purple',
  },
  enhanced: {
    label: 'Enhanced Permissions',
    description: 'These permissions add extra capabilities but aren\'t necessary for core functionality.',
    icon: 'plus-circle',
    color: 'blue',
  },
  admin_required: {
    label: 'Admin-Required Permissions',
    description: 'These require IT admin approval. Contact your admin if you\'d like to enable them.',
    icon: 'shield-alert',
    color: 'amber',
  },
};

/**
 * Links to Microsoft permission management pages
 */
export const PERMISSION_MANAGEMENT_LINKS = {
  /** My Apps - where users can manage their consented apps */
  myApps: 'https://myapps.microsoft.com',
  /** My Account - app permissions section */
  myAccount: 'https://myaccount.microsoft.com/permissions',
  /** Azure AD app consents (for admins) */
  azureADConsents: 'https://portal.azure.com/#blade/Microsoft_AAD_IAM/StartboardApplicationsMenuBlade/UserSettings',
  /** General Graph permissions reference */
  graphPermissionsRef: 'https://learn.microsoft.com/en-us/graph/permissions-reference',
};
